import { COMPACT_DENSITY_FACTOR } from "@mui/x-data-grid-premium"
import { log } from "helpers/logger"
import {
  BUFFER_WIDTH,
  GO_TO_FINDER_HEADER_CLASS,
  GO_TO_FINDER_WIDTH,
  HEADER_LINE_HEIGHT,
  HEADER_RESERVED_WIDTH,
  HEADER_VERTICAL_PADDING,
  METRIC_HEADER_FONT,
  MIN_LEAF_HEADER_HEIGHT,
  STACKED_ICONS_HEADER_HEIGHT,
} from "../constants"
import { getTextWidth } from "./text-measurement"

const TRAILING_PARENTHETICAL = /^(.*\S)\s+(\([^()]*\))$/
const SYMBOL_UNIT = /^\((?:[xz%]|\$[KM]?)\)$/
const CURRENCY_UNIT = /^\(([A-Z]{3})(?: [KM])?\)$/

function getSupportedCurrencyCodes(): ReadonlySet<string> {
  if (typeof Intl.supportedValuesOf === "function") return new Set(Intl.supportedValuesOf("currency"))
  log(
    new Error("Intl.supportedValuesOf is unavailable, so currency units in datagrid headers are kept in the header label"),
    { feature: "contributors-datagrid header units" },
  )
  return new Set()
}

const SUPPORTED_CURRENCY_CODES = getSupportedCurrencyCodes()

interface LayoutToken {
  text: string
  isBreakable: boolean
}

export interface HeaderLabelParts {
  words: string
  unit: string | null
}

export interface HeaderLayout {
  lineCount: number
  wordsClampLines: number | null
  isUnitOnOwnLine: boolean
}

function isUnit(parenthetical: string): boolean {
  if (SYMBOL_UNIT.test(parenthetical)) return true
  const currencyCode = CURRENCY_UNIT.exec(parenthetical)?.[1]
  return currencyCode !== undefined && SUPPORTED_CURRENCY_CODES.has(currencyCode)
}

export function splitHeaderUnit(headerName: string): HeaderLabelParts {
  const match = TRAILING_PARENTHETICAL.exec(headerName)
  return match && isUnit(match[2]) ? { words: match[1], unit: match[2] } : { words: headerName, unit: null }
}

export function getHeaderName(column: { headerName?: unknown, field: string }): string {
  return typeof column.headerName === "string" ? column.headerName : column.field
}

export function isGoToFinderColumn(column: { headerClassName?: unknown }): boolean {
  return column.headerClassName === GO_TO_FINDER_HEADER_CLASS
}

export function getHeaderLabelWidth(columnWidth: number, hasGoToFinder: boolean): number {
  return columnWidth - HEADER_RESERVED_WIDTH - (hasGoToFinder ? GO_TO_FINDER_WIDTH : 0)
}

export function getHeaderMinWidth(headerName: string, hasGoToFinder: boolean): number {
  return Math.ceil(measureText(headerName)) + HEADER_RESERVED_WIDTH + (hasGoToFinder ? GO_TO_FINDER_WIDTH : 0)
}

function measureText(text: string): number {
  return getTextWidth(text, METRIC_HEADER_FONT) - BUFFER_WIDTH
}

function countLines(tokens: LayoutToken[], availableWidth: number, breakLongWords: boolean): number {
  const lineWidth = Math.max(availableWidth, 1)
  const spaceWidth = measureText(" ")
  let lines = 1
  let usedWidth = 0

  const placeOnNewLine = (tokenWidth: number, isBreakable: boolean) => {
    const extraLines = breakLongWords && isBreakable ? Math.ceil(tokenWidth / lineWidth) - 1 : 0
    lines += extraLines
    usedWidth = Math.min(tokenWidth - extraLines * lineWidth, lineWidth)
  }

  tokens.forEach(({ text, isBreakable }) => {
    const tokenWidth = measureText(text)
    if (usedWidth === 0) {
      placeOnNewLine(tokenWidth, isBreakable)
    } else if (usedWidth + spaceWidth + tokenWidth <= lineWidth) {
      usedWidth += spaceWidth + tokenWidth
    } else {
      lines += 1
      placeOnNewLine(tokenWidth, isBreakable)
    }
  })
  return lines
}

export function getHeaderLayout(headerName: string, labelWidth: number, maxLines: number, breakLongWords = true): HeaderLayout {
  const { words, unit } = splitHeaderUnit(headerName)
  const wordTokens = words.split(/\s+/).filter(Boolean).map(text => ({ text, isBreakable: true }))
  const tokens = unit ? [...wordTokens, { text: unit, isBreakable: false }] : wordTokens
  const lineCount = countLines(tokens, labelWidth, breakLongWords)

  if (lineCount <= maxLines) {
    return { lineCount, wordsClampLines: null, isUnitOnOwnLine: false }
  }
  if (unit) {
    return { lineCount: maxLines, wordsClampLines: maxLines - 1, isUnitOnOwnLine: true }
  }
  return { lineCount: maxLines, wordsClampLines: maxLines, isUnitOnOwnLine: false }
}

export function getLeafHeaderHeight(lineCount: number, hasStackedIcons = false): number {
  const iconsHeight = hasStackedIcons ? STACKED_ICONS_HEADER_HEIGHT : 0
  return Math.max(MIN_LEAF_HEADER_HEIGHT, iconsHeight, lineCount * HEADER_LINE_HEIGHT + HEADER_VERTICAL_PADDING * 2)
}

export function getColumnHeaderHeightProp(leafHeaderHeight: number): number {
  let heightProp = Math.ceil(leafHeaderHeight / COMPACT_DENSITY_FACTOR)
  while (Math.floor(heightProp * COMPACT_DENSITY_FACTOR) < leafHeaderHeight) {
    heightProp += 1
  }
  return heightProp
}
